import { useState } from 'react';
import { ChatMarkdownRenderer } from '@/components/chat/ChatMarkdownRenderer';
import { toReadableMarkdown } from '@/lib/readable-markdown';
import type { AnalystMessage } from '@shared/types/company-analyst';

/** One message: the analyst's text in a card, its question set apart, then what it read and what changed in the sheet, folded. */
export const AnalystMessageView = ({ message }: { message: AnalystMessage }) => {
  const [open, setOpen] = useState(false);
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-domino-aubergine px-4 py-2.5 text-sm leading-6 text-white">{message.content}</p>
      </div>
    );
  }
  const readings = message.readings ?? [];
  const outcomes = message.outcomes ?? [];
  const notes = outcomes.filter((outcome) => outcome.note);
  const okReadings = readings.filter((reading) => reading.ok).length;
  return (
    <div className="rounded-2xl rounded-tl-md border border-domino-cardBorder bg-white/70 px-4 py-3">
      {message.failed
        ? <p className="text-sm leading-6 text-red-700">{message.content}</p>
        : <ChatMarkdownRenderer content={toReadableMarkdown(message.content)} className="text-sm leading-6 text-domino-ink [&_strong]:text-domino-aubergine" />}
      {message.question && !message.failed && (
        <p className="mt-3 whitespace-pre-wrap border-l-2 border-domino-aubergine pl-3 text-sm font-medium leading-6 text-domino-ink">{message.question}</p>
      )}
      {readings.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-domino-cardBorder pt-2">
          {readings.length > 0 && (
            <button type="button" onClick={() => setOpen(!open)} className="rounded-full bg-domino-muted px-2.5 py-0.5 text-xs text-domino-inkSoft hover:text-domino-ink">
              {okReadings} source{okReadings > 1 ? 's' : ''} lue{okReadings > 1 ? 's' : ''}{readings.length > okReadings ? ` · ${readings.length - okReadings} en échec` : ''}
            </button>
          )}
        </div>
      )}
      {open && (
        <div className="mt-2 space-y-1.5 rounded-lg bg-domino-muted px-3 py-2 text-xs leading-5 text-domino-inkSoft">
          {readings.map((reading) => (
            <p key={reading.id}>
              <span className={reading.ok ? 'text-emerald-700' : 'text-red-700'}>{reading.ok ? 'Lu' : 'Échec'}</span>{' · '}
              {reading.sourceUrl ? <a href={reading.sourceUrl} target="_blank" rel="noreferrer" className="underline">{reading.label}</a> : reading.label}
              {reading.error && !reading.ok ? ` — ${reading.error}` : ''}
            </p>
          ))}
          {notes.map((outcome, index) => <p key={`${outcome.op}-${index}`}>{outcome.note}</p>)}
        </div>
      )}
    </div>
  );
};
